/** @jsx jsx */
import { Badge } from "../components/Badge"
import { Header } from "../components/Header"
import { Incident, IncidentStatus } from "../types"
import { IncidentsTable } from "../components/IncidentsTable"
import { jsx, Text, Container, Box } from "theme-ui"
import { Layout } from "../components/layout"
import { Select } from "../components/Select"
import debounce from "lodash.debounce"
import moment, { Moment } from "moment"
import React from "react"
import SEO from "../components/seo"
import useIncidents from "../hooks/use-incidents"

const IndexPage: React.FC = () => {
  const [searchString, setSearchString] = React.useState<string>("")
  const [searchWords, setSearchWords] = React.useState<string[]>([])
  const [severityFilterOptions, setSeverityFilterOptions] = React.useState([])
  const rowIncidents: Incident[] = useIncidents()
  const [incidents, setIncidents] = React.useState<Incident[]>(rowIncidents)

  const isResolved = (status: IncidentStatus): boolean =>
    status === IncidentStatus.RESOLVED

  const openTickes = incidents.filter(i => isResolved(i.incidentStatusId))

  // I moved it to 90 days because 60 days returns no result
  const twoMonthAgo: Moment = moment().startOf("day").subtract(60, "day")

  const isWithin60Days = (date: string): boolean => {
    return moment(date).isAfter(twoMonthAgo)
  }

  const incidentsWithin30Days = incidents.filter(i =>
    isWithin60Days(i.createdOn)
  )

  const ignorableCharacters = /\W*/g

  const formatString = (raw: string) => {
    return raw?.toLowerCase().replace(ignorableCharacters, "")
  }

  const matchString = (haystack: string, needle: string) => {
    return formatString(haystack)?.includes(formatString(needle))
  }

  const filterBySearchTerm = (incidents, value: string) => {
    return incidents.filter(incident => {
      const { name, severity } = incident
      return (
        matchString(name, value) || matchString(severity?.description, value)
      )
    })
  }

  const filterBySeverity = (incidents, options) => {
    return incidents.filter(i => options?.some(o => o.value === i.severity?.id))
  }

  const filterBy = (options, value) => {
    let incidents = rowIncidents

    if (options?.length > 0) {
      incidents = filterBySeverity(incidents, options)
    }

    if (searchString) {
      setSearchWords(searchString.split(" "))
      incidents = filterBySearchTerm(incidents, value)
    }
    setIncidents(incidents)
  }

  const delayedQuery = React.useCallback(
    debounce(value => setSearchString(value), 300),
    []
  )

  React.useEffect(() => {
    filterBy(severityFilterOptions, searchString)
  }, [severityFilterOptions, searchString])

  return (
    <Layout>
      <SEO title="Home" />
      <Header onSearchChange={value => delayedQuery(value)} />
      <Container as="main">
        {incidents.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Text variant="h1" sx={{ color: "textDeactive", fontWeight: 600 }}>
              No results
            </Text>
          </Box>
        ) : (
          <React.Fragment>
            <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
              <Text variant="body" sx={{ mr: 8 }}>
                Open tickets
                <Badge
                  circle={true}
                  sx={{ ml: 2, position: "relative", top: "-2px" }}
                >
                  {openTickes.length}
                </Badge>
              </Text>
              <Text variant="body">
                Incidents within 60 days
                <Badge
                  circle={true}
                  sx={{ ml: 2, position: "relative", top: "-2px" }}
                >
                  {incidentsWithin30Days.length}
                </Badge>
              </Text>
              <Box sx={{ ml: "auto", width: 256 }}>
                <Select
                  onChange={options => setSeverityFilterOptions(options)}
                />
              </Box>
            </Box>
            <IncidentsTable incidents={incidents} searchWords={searchWords} />
          </React.Fragment>
        )}
      </Container>
    </Layout>
  )
}

export default IndexPage
